/**
 * Intent Router — détecte l'intention de recherche d'un visiteur entrant
 * via referrer, UTM ou query string, et expose un hook pour adapter l'UI
 * (bandeau, pré-remplissage devis, tracking).
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { trackGoal } from "@/lib/analytics";

export type IntentKind =
  | "price"
  | "compare"
  | "guide"
  | "urgent"
  | "local"
  | "brand"
  | "unknown";

export interface DetectedIntent {
  kind: IntentKind;
  query?: string;
  source?: string;
  city?: string;
  service?: string;
}

const PRICE_RX = /\b(prix|tarif|cout|coût|combien|سوم|ثمن|بشحال)\b/i;
const COMPARE_RX = /\b(vs|versus|comparat|meilleur|comparer|أحسن|مقارن)\b/i;
const GUIDE_RX = /\b(comment|guide|tutorial|tuto|كيفاش|كيف)\b/i;
const URGENT_RX = /\b(urgent|express|24h|48h|rapide|aujourd|today|عاجل|سريع)\b/i;
const LOCAL_RX = /\b(casablanca|rabat|marrakech|tanger|agadir|fes|fès|meknes|oujda|kenitra|tetouan|près de moi|near me|قربي)\b/i;
const BRAND_RX = /\b(conceptions\.ma|conceptions ma|adhorizon)\b/i;

const CITY_RX = /\b(casablanca|rabat|marrakech|tanger|agadir|fes|fès|meknes|oujda|kenitra|tetouan)\b/i;

function extractFromString(text: string): IntentKind {
  if (!text) return "unknown";
  const lower = text.toLowerCase();
  if (BRAND_RX.test(lower)) return "brand";
  if (URGENT_RX.test(lower)) return "urgent";
  if (PRICE_RX.test(lower)) return "price";
  if (COMPARE_RX.test(lower)) return "compare";
  if (GUIDE_RX.test(lower)) return "guide";
  if (LOCAL_RX.test(lower)) return "local";
  return "unknown";
}

function readSearchParam(search: string, key: string): string | undefined {
  try {
    const p = new URLSearchParams(search);
    return p.get(key) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Detect entry intent from referrer + URL params. Runs once per pathname change.
 * UTM-style hints take precedence (utm_term, q), then referrer, then path.
 */
export function detectIntent(): DetectedIntent {
  if (typeof window === "undefined") return { kind: "unknown" };
  const search = window.location.search;
  const utmTerm = readSearchParam(search, "utm_term");
  const q = readSearchParam(search, "q");
  const intentParam = readSearchParam(search, "intent");
  const queryText = utmTerm || q || "";
  const referrer = document.referrer || "";
  const path = window.location.pathname;

  // Explicit ?intent= wins
  if (intentParam) {
    const valid: IntentKind[] = ["price", "compare", "guide", "urgent", "local", "brand"];
    if (valid.includes(intentParam as IntentKind)) {
      return { kind: intentParam as IntentKind, query: queryText, source: "explicit" };
    }
  }

  // 1. Try query text from utm_term / q
  let kind = extractFromString(queryText);
  let source = queryText ? "utm" : undefined;

  // 2. Try referrer (Google SERP query is rarely available, but path of referrer is)
  if (kind === "unknown" && referrer) {
    kind = extractFromString(referrer);
    if (kind !== "unknown") source = "referrer";
  }

  // 3. Try path (someone landing on /tarifs/* is clearly price-intent)
  if (kind === "unknown") {
    kind = extractFromString(path);
    if (kind !== "unknown") source = "path";
  }

  // City extraction
  const cityMatch =
    queryText.match(CITY_RX)?.[0] ||
    path.match(CITY_RX)?.[0] ||
    referrer.match(CITY_RX)?.[0];

  return {
    kind,
    query: queryText || undefined,
    source,
    city: cityMatch?.toLowerCase(),
  };
}

/**
 * React hook : detects intent once on mount and on path change.
 * Tracks an `intent_captured` goal in analytics with the resolved kind.
 */
export function useIntent(): DetectedIntent {
  const location = useLocation();
  const [intent, setIntent] = useState<DetectedIntent>({ kind: "unknown" });

  useEffect(() => {
    const detected = detectIntent();
    setIntent(detected);
    if (detected.kind !== "unknown") {
      trackGoal("intent_captured", {
        kind: detected.kind,
        source: detected.source,
        city: detected.city,
        path: location.pathname,
      });
    }
  }, [location.pathname]);

  return intent;
}

/**
 * Suggest the best landing path for a given intent kind.
 * Used by IntentBanner to deep-link visitors to the right page.
 */
export function suggestPath(intent: DetectedIntent, fallback = "/services"): string {
  switch (intent.kind) {
    case "price":
      return "/tarifs/enseigne-lumineuse";
    case "compare":
      return "/comparer/enseigne-lumineuse-vs-non-lumineuse";
    case "guide":
      return "/guides/choisir-enseigne-commerce";
    case "urgent":
      return "/express/logo";
    case "local":
      return intent.city ? `/maroc/${intent.city}` : "/maroc/casablanca";
    default:
      return fallback;
  }
}
